import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { inject, Injector, ChangeDetectorRef, Directive, DestroyRef, EventEmitter, Output, Input, ViewEncapsulation, ChangeDetectionStrategy, Component, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { NzConfigService, onConfigChangeEventForComponent } from 'ng-zorro-antd/core/config';
import { NzSingletonService } from 'ng-zorro-antd/core/services';
import { toCssPixel } from 'ng-zorro-antd/core/util';
import { moveUpMotion } from 'ng-zorro-antd/core/animation';
import * as i2 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import * as i1 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
const _c0 = (a0, a1) => ({
  $implicit: a0,
  data: a1
});
function NzMessageComponent_Case_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-icon", 3);
  }
}
function NzMessageComponent_Case_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-icon", 4);
  }
}
function NzMessageComponent_Case_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-icon", 5);
  }
}
function NzMessageComponent_Case_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-icon", 6);
  }
}
function NzMessageComponent_Case_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-icon", 7);
  }
}
function NzMessageComponent_ng_container_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "span", 9);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("innerHTML", ctx_r0.instance.content, i0.ɵɵsanitizeHtml);
  }
}
function NzMessageContainerComponent_For_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "nz-message", 2);
    i0.ɵɵlistener("destroyed", function NzMessageContainerComponent_For_2_Template_nz_message_destroyed_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.remove($event.id, $event.userAction));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const instance_r3 = ctx.$implicit;
    i0.ɵɵproperty("instance", instance_r3);
  }
}
let globalCounter = 0;
class NzMNService {
  container;
  nzSingletonService = inject(NzSingletonService);
  overlay = inject(Overlay);
  injector = inject(Injector);
  remove(id) {
    if (this.container) {
      if (id) {
        this.container.remove(id);
      } else {
        this.container.removeAll();
      }
    }
  }
  getInstanceId() {
    return `${this.componentPrefix}-${globalCounter++}`;
  }
  withContainer(ctor) {
    let containerInstance = this.nzSingletonService.getSingletonWithKey(this.componentPrefix);
    if (containerInstance) {
      return containerInstance;
    }
    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy: this.overlay.position().global()
    });
    const componentPortal = new ComponentPortal(ctor, null, this.injector);
    const componentRef = overlayRef.attach(componentPortal);
    const overlayWrapper = overlayRef.hostElement;
    overlayWrapper.style.zIndex = '1010';
    if (!containerInstance) {
      this.container = containerInstance = componentRef.instance;
      this.nzSingletonService.registerSingletonWithKey(this.componentPrefix, containerInstance);
      this.container.afterAllInstancesRemoved.subscribe(() => {
        this.container = undefined;
        this.nzSingletonService.unregisterSingletonWithKey(this.componentPrefix);
        overlayRef.dispose();
      });
    }
    return containerInstance;
  }
}
let NzMNContainerComponent = /*#__PURE__*/(() => {
  class NzMNContainerComponent {
    config;
    instances = [];
    _afterAllInstancesRemoved = new Subject();
    afterAllInstancesRemoved = this._afterAllInstancesRemoved.asObservable();
    cdr = inject(ChangeDetectorRef);
    nzConfigService = inject(NzConfigService);
    constructor() {
      this.subscribeConfigChange();
    }
    create(data) {
      const instance = this.onCreate(data);
      if (this.instances.length >= this.config.nzMaxStack) {
        this.instances = this.instances.slice(1);
      }
      this.instances = [...this.instances, instance];
      this.readyInstances();
      return instance;
    }
    remove(id, userAction = false) {
      this.instances.map((instance, index) => ({
        index,
        instance
      })).filter(({
        instance
      }) => instance.messageId === id).forEach(({
        index,
        instance
      }) => {
        this.instances.splice(index, 1);
        this.instances = [...this.instances];
        this.onRemove(instance, userAction);
        this.readyInstances();
      });
      if (!this.instances.length) {
        this.onAllInstancesRemoved();
      }
    }
    removeAll() {
      this.instances.forEach(i => this.onRemove(i, false));
      this.instances = [];
      this.readyInstances();
      this.onAllInstancesRemoved();
    }
    onCreate(instance) {
      instance.options = this.mergeOptions(instance.options);
      instance.onClose = new Subject();
      return instance;
    }
    onRemove(instance, userAction) {
      instance.onClose.next(userAction);
      instance.onClose.complete();
    }
    onAllInstancesRemoved() {
      this._afterAllInstancesRemoved.next();
      this._afterAllInstancesRemoved.complete();
    }
    readyInstances() {
      this.cdr.detectChanges();
    }
    mergeOptions(options) {
      const {
        nzDuration,
        nzAnimate,
        nzPauseOnHover
      } = this.config;
      return {
        nzDuration,
        nzAnimate,
        nzPauseOnHover,
        ...options
      };
    }
    static ɵfac = function NzMNContainerComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzMNContainerComponent)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzMNContainerComponent
    });
  }
  return NzMNContainerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzMNComponent = /*#__PURE__*/(() => {
  class NzMNComponent {
    cdr = inject(ChangeDetectorRef);
    destroyRef = inject(DestroyRef);
    animationStateChanged = new Subject();
    options;
    autoClose;
    closeTimer;
    userAction = false;
    eraseTimer;
    eraseTimingStart;
    eraseTTL;
    constructor() {
      this.destroyRef.onDestroy(() => {
        if (this.autoClose) {
          this.clearEraseTimeout();
        }
        this.animationStateChanged.complete();
      });
    }
    ngOnInit() {
      this.options = this.instance.options;
      if (this.options.nzAnimate) {
        this.instance.state = 'enter';
        this.animationStateChanged.pipe(filter(event => event.phaseName === 'done' && event.toState === 'leave'), take(1)).subscribe(() => {
          clearTimeout(this.closeTimer);
          this.destroyed.next({
            id: this.instance.messageId,
            userAction: this.userAction
          });
        });
      }
      this.autoClose = this.options.nzDuration > 0;
      if (this.autoClose) {
        this.initErase();
        this.startEraseTimeout();
      }
    }
    onEnter() {
      if (this.autoClose && this.options.nzPauseOnHover) {
        this.clearEraseTimeout();
        this.updateTTL();
      }
    }
    onLeave() {
      if (this.autoClose && this.options.nzPauseOnHover) {
        this.startEraseTimeout();
      }
    }
    destroy(userAction = false) {
      this.userAction = userAction;
      if (this.options.nzAnimate) {
        this.instance.state = 'leave';
        this.cdr.detectChanges();
        this.closeTimer = setTimeout(() => {
          this.closeTimer = undefined;
          this.destroyed.next({
            id: this.instance.messageId,
            userAction
          });
        }, 200);
      } else {
        this.destroyed.next({
          id: this.instance.messageId,
          userAction
        });
      }
    }
    initErase() {
      this.eraseTTL = this.options.nzDuration;
      this.eraseTimingStart = Date.now();
    }
    updateTTL() {
      if (this.autoClose) {
        this.eraseTTL -= Date.now() - this.eraseTimingStart;
      }
    }
    startEraseTimeout() {
      if (this.eraseTTL > 0) {
        this.clearEraseTimeout();
        this.eraseTimer = setTimeout(() => this.destroy(), this.eraseTTL);
        this.eraseTimingStart = Date.now();
      } else {
        this.destroy();
      }
    }
    clearEraseTimeout() {
      if (this.eraseTimer !== null) {
        clearTimeout(this.eraseTimer);
        this.eraseTimer = undefined;
      }
    }
    static ɵfac = function NzMNComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzMNComponent)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzMNComponent
    });
  }
  return NzMNComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzMessageComponent = /*#__PURE__*/(() => {
  class NzMessageComponent extends NzMNComponent {
    instance;
    destroyed = new EventEmitter();
    index;
    static ɵfac = /* @__PURE__ */(() => {
      let ɵNzMessageComponent_BaseFactory;
      return function NzMessageComponent_Factory(__ngFactoryType__) {
        return (ɵNzMessageComponent_BaseFactory || (ɵNzMessageComponent_BaseFactory = i0.ɵɵgetInheritedFactory(NzMessageComponent)))(__ngFactoryType__ || NzMessageComponent);
      };
    })();
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzMessageComponent,
      selectors: [["nz-message"]],
      inputs: {
        instance: "instance"
      },
      outputs: {
        destroyed: "destroyed"
      },
      exportAs: ["nzMessage"],
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 9,
      vars: 9,
      consts: [[1, "ant-message-notice", 3, "mouseenter", "mouseleave"], [1, "ant-message-notice-content"], [1, "ant-message-custom-content"], ["nzType", "check-circle"], ["nzType", "info-circle"], ["nzType", "exclamation-circle"], ["nzType", "close-circle"], ["nzType", "loading"], [4, "nzStringTemplateOutlet", "nzStringTemplateOutletContext"], [3, "innerHTML"]],
      template: function NzMessageComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0);
          i0.ɵɵlistener("@moveUpMotion.done", function NzMessageComponent_Template_div_animation_moveUpMotion_done_0_listener($event) {
            return ctx.animationStateChanged.next($event);
          })("mouseenter", function NzMessageComponent_Template_div_mouseenter_0_listener() {
            return ctx.onEnter();
          })("mouseleave", function NzMessageComponent_Template_div_mouseleave_0_listener() {
            return ctx.onLeave();
          });
          i0.ɵɵelementStart(1, "div", 1)(2, "div", 2);
          i0.ɵɵconditionalCreate(3, NzMessageComponent_Case_3_Template, 1, 0, "nz-icon", 3)(4, NzMessageComponent_Case_4_Template, 1, 0, "nz-icon", 4)(5, NzMessageComponent_Case_5_Template, 1, 0, "nz-icon", 5)(6, NzMessageComponent_Case_6_Template, 1, 0, "nz-icon", 6)(7, NzMessageComponent_Case_7_Template, 1, 0, "nz-icon", 7);
          i0.ɵɵtemplate(8, NzMessageComponent_ng_container_8_Template, 2, 1, "ng-container", 8);
          i0.ɵɵelementEnd()()();
        }
        if (rf & 2) {
          let tmp_2_0;
          i0.ɵɵproperty("@moveUpMotion", ctx.instance.state);
          i0.ɵɵadvance(2);
          i0.ɵɵclassMap("ant-message-" + ctx.instance.type);
          i0.ɵɵadvance();
          i0.ɵɵconditional((tmp_2_0 = ctx.instance.type) === "success" ? 3 : tmp_2_0 === "info" ? 4 : tmp_2_0 === "warning" ? 5 : tmp_2_0 === "error" ? 6 : tmp_2_0 === "loading" ? 7 : -1);
          i0.ɵɵadvance(5);
          i0.ɵɵproperty("nzStringTemplateOutlet", ctx.instance.content)("nzStringTemplateOutletContext", i0.ɵɵpureFunction2(6, _c0, ctx, ctx.instance.options == null ? null : ctx.instance.options.nzData));
        }
      },
      dependencies: [NzIconModule, i1.NzIconDirective, NzOutletModule, i2.NzStringTemplateOutletDirective],
      encapsulation: 2,
      data: {
        animation: [moveUpMotion]
      },
      changeDetection: 0
    });
  }
  return NzMessageComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const NZ_CONFIG_COMPONENT_NAME = 'message';
const NZ_MESSAGE_DEFAULT_CONFIG = {
  nzAnimate: true,
  nzDuration: 3000,
  nzMaxStack: 7,
  nzPauseOnHover: true,
  nzTop: 24,
  nzDirection: 'ltr'
};
let NzMessageContainerComponent = /*#__PURE__*/(() => {
  class NzMessageContainerComponent extends NzMNContainerComponent {
    dir = this.nzConfigService.getConfigForComponent(NZ_CONFIG_COMPONENT_NAME)?.nzDirection || 'ltr';
    top;
    constructor() {
      super();
      this.updateConfig();
    }
    subscribeConfigChange() {
      onConfigChangeEventForComponent(NZ_CONFIG_COMPONENT_NAME, () => {
        this.updateConfig();
        this.dir = this.nzConfigService.getConfigForComponent(NZ_CONFIG_COMPONENT_NAME)?.nzDirection || this.dir;
      });
    }
    updateConfig() {
      this.config = {
        ...NZ_MESSAGE_DEFAULT_CONFIG,
        ...this.config,
        ...this.nzConfigService.getConfigForComponent(NZ_CONFIG_COMPONENT_NAME)
      };
      this.top = toCssPixel(this.config.nzTop);
      this.cdr.markForCheck();
    }
    static ɵfac = function NzMessageContainerComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzMessageContainerComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzMessageContainerComponent,
      selectors: [["nz-message-container"]],
      exportAs: ["nzMessageContainer"],
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 3,
      vars: 4,
      consts: [[1, "ant-message"], [3, "instance"], [3, "destroyed", "instance"]],
      template: function NzMessageContainerComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0);
          i0.ɵɵrepeaterCreate(1, NzMessageContainerComponent_For_2_Template, 1, 1, "nz-message", 1, i0.ɵɵrepeaterTrackByIdentity);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵstyleProp("top", ctx.top);
          i0.ɵɵclassProp("ant-message-rtl", ctx.dir === "rtl");
          i0.ɵɵadvance();
          i0.ɵɵrepeater(ctx.instances);
        }
      },
      dependencies: [NzMessageComponent],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzMessageContainerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzMessageService = /*#__PURE__*/(() => {
  class NzMessageService extends NzMNService {
    componentPrefix = 'message-';
    success(content, options) {
      return this.createInstance({
        type: 'success',
        content
      }, options);
    }
    error(content, options) {
      return this.createInstance({
        type: 'error',
        content
      }, options);
    }
    info(content, options) {
      return this.createInstance({
        type: 'info',
        content
      }, options);
    }
    warning(content, options) {
      return this.createInstance({
        type: 'warning',
        content
      }, options);
    }
    loading(content, options) {
      return this.createInstance({
        type: 'loading',
        content
      }, options);
    }
    create(type, content, options) {
      return this.createInstance({
        type,
        content
      }, options);
    }
    createInstance(message, options) {
      this.container = this.withContainer(NzMessageContainerComponent);
      return this.container.create({
        ...message,
        ...{
          createdAt: new Date(),
          messageId: this.getInstanceId(),
          options
        }
      });
    }
    static ɵfac = /* @__PURE__ */(() => {
      let ɵNzMessageService_BaseFactory;
      return function NzMessageService_Factory(__ngFactoryType__) {
        return (ɵNzMessageService_BaseFactory || (ɵNzMessageService_BaseFactory = i0.ɵɵgetInheritedFactory(NzMessageService)))(__ngFactoryType__ || NzMessageService);
      };
    })();
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NzMessageService,
      factory: NzMessageService.ɵfac,
      providedIn: 'root'
    });
  }
  return NzMessageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzMNComponent, NzMNContainerComponent, NzMNService, NzMessageComponent, NzMessageContainerComponent, NzMessageService };
